import { PlusOutlined, DeleteOutlined } from '@easyv/react-icons';
import * as React from 'react';
import { set } from 'lodash-es';
import classNames from 'classnames';
import {
  StateJson,
  Component,
  CustomComponentManager,
  EditorActionsManager,
  UpdateStateAction,
  RemoveStateAction,
  AddStateAction,
  RenameStateAction,
  SetDefaultStateAction,
  SortStateAction,
} from '@easytwin/runtime';
import { observer } from 'mobx-react-lite';
import { deepClone, getTypeVersionKey, isNil } from '@/utils';
import { UISchema } from '@/type';
import { useCopyStateStore } from '@/hooks';
import { Primitive, CollapseBox } from '@/UIGenerator';
import { UpgradeVersionIcon } from '../UpgradeVersionIcon';
import { SortableStateTabs } from './StateTabs';
import { HoverState } from './StateTab';

export interface StateTabsPanelProps {
  value: StateJson[];
  comp: Component;
  formSchema: UISchema;
  allowEmpty?: boolean;
  /** for exception */
  title?: string;
  //是否需要空状态填充
  isEmptyStateFill?: boolean;
  //删除按钮是否一直为可点击状态
  alwaysUsable?: boolean;
}

export const StateTabsPanel: React.FC<React.PropsWithChildren<StateTabsPanelProps>> = observer(
  (props) => {
    const {
      value,
      comp,
      title = '状态',
      formSchema,
      allowEmpty = false,
      isEmptyStateFill = true,
      alwaysUsable = false,
    } = props;
    const [renamingStateId, setRenamingStateId] = React.useState<string>();

    const selectedStateId = comp.selectedStateId;
    const sortedValue = React.useMemo(() => {
      return value.sort((a, b) => a.rank - b.rank);
    }, [value]);
    const curStateConfig = sortedValue.find((a) => a.id === selectedStateId)?.config;

    const { clipboard, setClipboard } = useCopyStateStore();
    const oldStateConfigRef = React.useRef<any>();
    const isEditing = React.useRef<boolean>(false);

    console.log('cur selected id', comp.name, comp.selectedStateId, sortedValue);

    const enableDelete = alwaysUsable
      ? alwaysUsable
      : sortedValue && (allowEmpty ? sortedValue.length > 0 : sortedValue.length > 1);

    const module = comp.engine.getManager(CustomComponentManager).get(comp.type, comp.version);
    const isFuncComp = module?.Settings?.category === 'FunctionalComponent' || false;

    const editorActionMgr = comp.engine.getManager(EditorActionsManager);

    React.useEffect(() => {
      if (selectedStateId === undefined && sortedValue && sortedValue.length > 0) {
        comp.selectState(sortedValue[0].id);
        console.log('默认选中', comp.name, sortedValue[0].id);
      }
    }, [comp, selectedStateId, sortedValue]);

    const handleRenameStart = (id: string) => {
      setRenamingStateId(id);
    };

    const handleRenameStateEnd = (name: string) => {
      if (!renamingStateId) return;
      editorActionMgr.execute(new RenameStateAction(comp, renamingStateId, name));
      setRenamingStateId(undefined);
    };

    const onStateUpdate = (name: string, v: unknown) => {
      if (!selectedStateId) return;

      if (isEditing.current === false) {
        isEditing.current = true;
        // 由于onStateUpdate会修改组件的states数据，所以无法在editorAction中拿到旧数据，只能提前储存
        oldStateConfigRef.current = deepClone(curStateConfig);
      }
      const newState = deepClone(curStateConfig);
      set(newState, name, v);

      comp.updateState(selectedStateId, newState, false);
    };

    const onStateConfirm = (name: string, value: unknown) => {
      if (!selectedStateId || !editorActionMgr) return;
      isEditing.current = false;
      const newState = deepClone(curStateConfig);
      set(newState, name, value);

      // 如果是输入框输入的撤回就读取oldStateConfigRef.current，如果是没有update直接confirm的组件（如select、checkbox）则直接获取curStateConfig
      const oldStateConfig = oldStateConfigRef.current || curStateConfig;
      oldStateConfigRef.current = undefined;
      editorActionMgr.execute(
        new UpdateStateAction(comp, selectedStateId, newState, oldStateConfig),
      );
    };

    const children = formSchema.map((a, index) => (
      <Primitive
        key={index}
        target={curStateConfig}
        schema={a}
        comp={comp}
        onChange={onStateUpdate}
        onConfirm={onStateConfirm}
      />
    ));

    const handleSortState = (stateId: string, toId: string, hoverState: HoverState) => {
      const editorActionMgr = comp.engine.getManager(EditorActionsManager);
      if (!editorActionMgr || hoverState === 'none') return;
      console.log('sort state');
      // 计算排序需要的参数
      let oldRank: number, rank: number;
      const srcStateIndex = sortedValue.findIndex((a) => a.id === stateId);
      if (srcStateIndex === -1) {
        console.error(`comp上不存在此状态id${stateId}`);
        return;
      }

      const toStateIndex = sortedValue.findIndex((a) => a.id === toId);
      if (toStateIndex === -1) {
        console.error(`comp上不存在此状态id${toId}`);
        return;
      }

      oldRank = sortedValue[srcStateIndex].rank;
      const n = sortedValue.length;
      if (hoverState === 'left') {
        if (toStateIndex === 0) {
          rank = sortedValue[toStateIndex].rank / 2;
        } else {
          const leftRank: number | undefined = sortedValue[toStateIndex - 1].rank;
          const rightRank: number | undefined = sortedValue[toStateIndex].rank;
          rank = (leftRank + rightRank) / 2;
        }
      } else {
        if (toStateIndex === n - 1) {
          rank = sortedValue[toStateIndex].rank + 1000;
        } else {
          const leftRank: number | undefined = sortedValue[toStateIndex].rank;
          const rightRank: number | undefined = sortedValue[toStateIndex + 1].rank;
          rank = (leftRank + rightRank) / 2;
        }
      }
      editorActionMgr.execute(new SortStateAction(comp, stateId, rank, oldRank));
    };

    return (
      <CollapseBox
        noStyle
        showDivider
        disable
        title={title}
        type='area'
        titleExtra={
          <>
            {isFuncComp && <UpgradeVersionIcon comp={comp} />}
            <PlusOutlined
              className='hover:text-white text-baseText p-1 text-base'
              onClick={async (e) => {
                e.stopPropagation();
                editorActionMgr.execute(new AddStateAction(comp));
              }}
            />
            <DeleteOutlined
              className={classNames(
                'p-1 text-base',
                enableDelete
                  ? 'hover:text-white text-baseText'
                  : 'cursor-not-allowed text-baseTextDisabled ',
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (!selectedStateId || !enableDelete) return;
                editorActionMgr.execute(new RemoveStateAction(comp, selectedStateId));
              }}
            />
          </>
        }
      >
        {sortedValue && sortedValue.length ? (
          <div>
            <div>
              <SortableStateTabs
                allowEmpty={allowEmpty}
                dataSource={sortedValue}
                selectKey={selectedStateId}
                disablePaste={
                  isNil(clipboard) ||
                  clipboard.typeVersion !== getTypeVersionKey(comp.type, comp.version)
                }
                renamingStateId={renamingStateId}
                onTabChange={(id) => {
                  comp.selectState(id);
                }}
                onSetDefaultState={(id) => {
                  const editorActionMgr = comp.engine.getManager(EditorActionsManager);
                  if (!editorActionMgr) return;
                  editorActionMgr.execute(new SetDefaultStateAction(comp, id));
                }}
                onPasteState={(id) => {
                  if (!clipboard) return;
                  if (getTypeVersionKey(comp.type, comp.version) !== clipboard.typeVersion) return;
                  const editorActionMgr = comp.engine.getManager(EditorActionsManager);
                  if (!editorActionMgr) return;
                  const oldStateConfig = comp._states.find((a) => a.id === id)?.config;
                  if (!oldStateConfig) return;
                  editorActionMgr.execute(
                    new UpdateStateAction(comp, id, clipboard.data.config, oldStateConfig),
                  );
                }}
                onCopyState={(id) => {
                  const state = sortedValue.find((a) => a.id === id);
                  if (!state) return;
                  setClipboard(state, comp.type, comp.version);
                }}
                onDeleteState={(id) => {
                  if (!editorActionMgr) return;
                  editorActionMgr.execute(new RemoveStateAction(comp, id));
                }}
                onRenameStateStart={handleRenameStart}
                onRenameStateEnd={handleRenameStateEnd}
                onSortState={handleSortState}
              />
            </div>
            {children}
          </div>
        ) : (
          isEmptyStateFill && (
            <div className='px-4 py-6 text-center text-textColorSecondary flex items-center justify-center'>
              <span>未配置状态，点击</span>
              <PlusOutlined
                className='text-white hover:text-primary px-1 cursor-pointer'
                onClick={async (e) => {
                  e.stopPropagation();
                  editorActionMgr.execute(new AddStateAction(comp));
                }}
              />
              <span>创建</span>
            </div>
          )
        )}
      </CollapseBox>
    );
  },
);
